import { useEffect } from "react";
import {
  BrowserRouter as Router,
  useLocation,
} from "react-router";
import { AppRoutes } from "./components/routing/AppRoutes";
import { FaviconManager } from "./components/FaviconManager";
import { SeoHead } from "./components/SeoHead";
import { CookieConsent } from "./components/CookieConsent";
import { Toaster } from "./components/ui/sonner";
import { ConferenceBanner } from "./components/ConferenceBanner";
import {
  NavigationProvider,
  useNavigation,
} from "./contexts/NavigationContext";
import { NavigationLoadingOverlay } from "./components/PageTransition";
import { useAnalyticsPageViews } from "./hooks/useAnalyticsPageViews";
import { useRouteScroll } from "./hooks/useRouteScroll";
import { captureAttribution } from "./utils/attribution";

// Main App Content Component
function AppContent() {
  const { isNavigating } = useNavigation();
  const location = useLocation();

  // Check if current route is admin panel
  const isAdminRoute = location.pathname.startsWith(
    "/admin-qa-management-portal",
  );

  /*
   * The unlisted decks are shown full-screen to clients: the conference banner
   * and the cookie bar both sit over the slides, so neither is mounted there.
   */
  const isDeckRoute = location.pathname.startsWith("/deck/");

  // Check if current route is an article page - COMPLETELY disable loading spinner for articles
  const isArticleRoute =
    location.pathname.startsWith("/blog/article/");

  // GA4 page views, staff traffic on the admin portal excluded
  useAnalyticsPageViews(!isAdminRoute);

  // The campaign a visitor came from, kept for the tab so an enquiry sent pages
  // later still carries it. Independent of analytics consent: it is stored in
  // the browser and only leaves it inside a form the visitor chose to send.
  useEffect(() => {
    if (isAdminRoute) return;
    captureAttribution(window.location.href, document.referrer);
  }, [isAdminRoute, location.pathname, location.search]);

  // Top of the page on a plain route change; the target section when the URL
  // carries a hash, once the lazy page chunk has mounted it.
  useRouteScroll();

  return (
    <div>
      <FaviconManager />
      {/* Title, description, canonical, social cards and JSON-LD, from
          config/seo.ts. Above the routes so a page that owns its own metadata
          still overrides it -- see components/SeoHead.tsx. */}
      <SeoHead />
      {!isAdminRoute && !isDeckRoute && <ConferenceBanner />}
      <AppRoutes />
      {!isArticleRoute && (
        <NavigationLoadingOverlay isLoading={isNavigating} />
      )}
      {!isAdminRoute && !isDeckRoute && <CookieConsent />}
      {/* Mounted once for the whole app; toast() writes into this. */}
      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <NavigationProvider>
      <Router>
        <AppContent />
      </Router>
    </NavigationProvider>
  );
}